import {
   ChangeDetectionStrategy,
   Component,
   EventEmitter,
   Input,
   Output,
   ViewChild,
   ViewEncapsulation,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { CallState } from '@lcs/shared/api';
import { MailboxInput, PromptTypes } from 'generated/graphql-types';
import { DetailView } from 'src/app/common-components/detail/detail-view';
import { AddNewPromptEvent } from '../../extension-details/models/add-new-prompt-event.model';
import { ExtensionPrivileges } from '../../extension-details/models/extension-privileges';
import { MailboxPrivileges } from '../../mailbox-register/models/mailbox-privileges';
import { PromptPrivileges } from '../../prompt-register/models/prompt-privileges';
import { MailboxDetailSaveEvent } from '../models/mailbox-detail-form-submit-event';
import { MailboxDetailView } from '../models/mailbox-detail-view.model';
import { VoiceMailGreetingPromptOption } from '../models/voicemail-greeting-prompt-option.model';
import { MailboxDetailFormService } from '../services/mailbox-detail-form.service';
import { MailboxDetailComponent } from './mailbox-detail-sections/mailbox-detail-view.component';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    selector: 'ndt-mailbox-detail-view',
    styleUrls: ['./mailbox-standalone-detail-view.component.scss'],
    templateUrl: './mailbox-standalone-detail-view.component.html',
    standalone: false
})
export class MailboxDetailViewComponent extends DetailView {
   @Input() get mailbox(): MailboxDetailView | null {
      return this._mailbox;
   }

   set mailbox(value: MailboxDetailView | null) {
      this._mailbox = value;
      if (value) {
         this.formService.updateMailbox(value);
      }
   }

   @Input() set mailboxPrivileges(privileges: MailboxPrivileges) {
      this._mailboxPrivileges = privileges;
      if (!privileges?.canEdit) {
         this.form.disable();
      }
   }

   get mailboxPrivileges(): MailboxPrivileges {
      return this._mailboxPrivileges;
   }
   get isSmartVoicemail(): boolean {
      return this.mailbox?.extension?.extensionType?.name === 'SmartVoicemail';
   }

   @Input() callState: CallState;

   @Input() set promptPrivileges(privileges: PromptPrivileges) {
      this._promptPrivileges = privileges;
   }

   @Input() set extensionPrivileges(privileges: ExtensionPrivileges) {
      this._extensionPrivileges = privileges;
   }

   @ViewChild(MailboxDetailComponent)
   private mailboxDetail: MailboxDetailComponent;

   @Output() formSaveCanceled = new EventEmitter<void>();

   @Output() formSubmitted = new EventEmitter<MailboxDetailSaveEvent>();

   @Output() deleteMailbox = new EventEmitter<{ mailboxId: number }>();

   @Output() addNewPrompt = new EventEmitter<AddNewPromptEvent>();

   @Output() editMailboxPin = new EventEmitter<{ mailboxId: number }>();

   @Output() clearMailboxClick = new EventEmitter<number[]>();
   @Output() resetMailboxClick = new EventEmitter<{
      mailboxId: number;
      mailboxMessageIds: number[];
   }>();

   form: UntypedFormGroup;

   private _mailbox: MailboxDetailView | null;

   private _mailboxPrivileges: MailboxPrivileges;

   private _promptPrivileges: PromptPrivileges;

   private _extensionPrivileges;

   constructor(public formService: MailboxDetailFormService, private router: Router) {
      super();
      this.form = this.formService.form;
   }

   ngOnInit(): void {
      if (!this._extensionPrivileges.canEditOthers) {
         this.form.get('namePrompt')?.disable();
         this.form.get('unavailablePrompt')?.disable();
         this.form.get('busyPrompt')?.disable();
         this.form.get('temporaryUnavailablePrompt')?.disable();
         this.form.get('useTemporaryGreeting')?.disable();
      }
   }

   onSubmit(): void {
      const submitEvent = this.getFormSubmittedEvent();
      if (submitEvent) {
         this.formSubmitted.emit(submitEvent);
      }
   }

   onCancel(): void {
      this.formSaveCanceled.emit();
   }

   canAddPrompts(): boolean {
      return this._promptPrivileges.canAdd;
   }

   onAddNewVoiceMailGreetingPromptClick(event: VoiceMailGreetingPromptOption): void {
      this.addNewPrompt.emit({
         promptType: PromptTypes.VoiceMailGreeting,
         voiceMailGreetingPrompt: event,
      });
   }

   onEditMailboxPinClick(): void {
      if (this.mailbox?.mailboxId) {
         this.editMailboxPin.emit({
            mailboxId: this.mailbox.mailboxId,
         });
      }
   }

   private getFormSubmittedEvent(): MailboxDetailSaveEvent | null {
      if (!this.formService.form.dirty || !this.mailbox) {
         return null;
      }
      const mailbox = this.formService.getValue(this.mailbox.mailboxId);
      let mailboxFields: (keyof MailboxInput)[] = [];
      mailboxFields = this.formService.addFieldForDirtyControl(mailboxFields, 'mailbox.password', 'password');
      mailboxFields = this.formService.addFieldForDirtyControl(mailboxFields, 'mailbox.emailAddress', 'emailAddress');
      mailboxFields = this.formService.addFieldForDirtyControl(
         mailboxFields,
         'mailbox.markReadAfterEmail',
         'markReadAfterEmail'
      );
      mailboxFields = this.formService.addFieldForDirtyControl(
         mailboxFields,
         'mailbox.discardAudioAfterEmail',
         'discardAudioAfterEmail'
      );
      mailboxFields = this.formService.addFieldForDirtyControl(mailboxFields, 'mailbox.sayCallerId', 'sayCallerId');
      mailboxFields = this.formService.addFieldForDirtyControl(
         mailboxFields,
         'mailbox.isInNameDirectory',
         'isInNameDirectory'
      );
      mailboxFields = this.formService.addFieldForDirtyControl(
         mailboxFields,
         'mailbox.unavailablePrompt',
         'unavailablePromptId2'
      );
      mailboxFields = this.formService.addFieldForDirtyControl(mailboxFields, 'mailbox.namePrompt', 'namePromptId');
      mailboxFields = this.formService.addFieldForDirtyControl(mailboxFields, 'mailbox.busyPrompt', 'busyPromptId');
      if (this.form.get('mailbox.temporaryUnavailablePrompt')?.dirty) {
         mailboxFields = [...mailboxFields, 'temporaryUnavailablePromptId'];
      }
      mailboxFields = this.formService.addFieldForDirtyControl(
         mailboxFields,
         'mailbox.operatorExtension',
         'operatorExtensionId'
      );
      return { mailbox, mailboxFields };
   }

   canResetMailbox(): boolean {
      const ids = this.mailboxDetail?.getMailboxMessageIds() ?? [];
      const mailboxHasPrompts = this.mailboxDetail?.mailboxHasPrompts();

      return ids.length || mailboxHasPrompts ? true : false;
   }

   canClearMailbox(): boolean {
      const ids = this.mailboxDetail?.getMailboxMessageIds() ?? [];
      return ids.length > 0;
   }

   clearMailbox() {
      const ids = this.mailboxDetail?.getMailboxMessageIds() ?? [];
      this.clearMailboxClick.emit(ids);
   }

   resetMailbox() {
      const ids = this.mailboxDetail?.getMailboxMessageIds() ?? [];
      const mailboxId = this.mailboxDetail?.getMailboxId();
      this.resetMailboxClick.emit({
         mailboxId,
         mailboxMessageIds: ids,
      });
   }
}
